import { useEffect, useState } from "react";
import { API_URL } from "../config";

export type BackendState = "idle" | "starting" | "ready" | "error";

// Mirror of the main-process backend state so the UI (status dot) can react
// without each component wiring IPC itself.
let currentState: BackendState = "idle";
let currentApiBase = "";
const listeners = new Set<(s: BackendState) => void>();

const setState = (s: BackendState) => {
  currentState = s;
  listeners.forEach((l) => l(s));
};

// In Electron, subscribe to backend-status pushes from main. In the browser
// the "backend" is the deployed API, always considered ready.
if (typeof window !== "undefined" && window.electronAPI) {
  window.electronAPI.onBackendStatus(({ state }) => setState(state as BackendState));
  window.electronAPI
    .getBackendStatus()
    .then(({ state, apiUrl }) => {
      currentApiBase = apiUrl;
      setState(state as BackendState);
    })
    .catch(() => undefined);
} else {
  currentState = "ready";
  currentApiBase = API_URL;
}

/**
 * Ensure the processing engine is available and return its base URL. In
 * Electron this lazily starts the Flask sidecar (first call pays the startup
 * cost); in the browser it resolves immediately to the deployed API.
 */
export const ensureBackend = async (): Promise<string> => {
  if (window.electronAPI?.ensureBackend) {
    const { apiUrl } = await window.electronAPI.ensureBackend();
    currentApiBase = apiUrl;
    return apiUrl;
  }
  return API_URL;
};

export const getApiBase = (): string => currentApiBase || API_URL;

/** Subscribe to backend state changes. Returns an unsubscribe fn. */
export const subscribeBackend = (cb: (s: BackendState) => void): (() => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** React hook for the current backend state. */
export const useBackendStatus = (): BackendState => {
  const [state, setStateHook] = useState<BackendState>(currentState);
  useEffect(() => subscribeBackend(setStateHook), []);
  return state;
};
